import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import type { Params } from "@/lib/amortizacion";

export interface Errores {
  monto?: string;
  tasa?: string;
  cuotas?: string;
}

export function validar(p: Params): Errores {
  const e: Errores = {};
  if (!(p.monto > 0)) e.monto = "El monto debe ser mayor a $0.00";
  if (!(p.tasaMensual >= 0) || Number.isNaN(p.tasaMensual))
    e.tasa = "La tasa debe ser mayor o igual a 0";
  if (!Number.isInteger(p.cuotas) || p.cuotas < 1 || p.cuotas > 120)
    e.cuotas = "El número de cuotas debe ser un entero entre 1 y 120";
  return e;
}

interface Props {
  params: Params;
  onChange: (p: Params) => void;
  errores: Errores;
  etiquetaPlazo?: string;
}

export function ParamsPanel({
  params,
  onChange,
  errores,
  etiquetaPlazo = "Número de cuotas (meses)",
}: Props) {
  const set = (patch: Partial<Params>) => onChange({ ...params, ...patch });
  return (
    <Card className="print-block border-navy/15 bg-card p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <span className="h-4 w-1 rounded-full bg-navy" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
          Parámetros del crédito
        </h3>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="monto">Monto del crédito (MXN)</Label>
          <Input
            id="monto"
            type="number"
            inputMode="decimal"
            min={0}
            step={100}
            value={Number.isNaN(params.monto) ? "" : params.monto}
            onChange={(ev) => set({ monto: ev.target.valueAsNumber })}
            className="num"
          />
          {errores.monto && <p className="text-xs text-destructive">{errores.monto}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tasa">Tasa de interés mensual (%)</Label>
          <Input
            id="tasa"
            type="number"
            inputMode="decimal"
            min={0}
            step={0.01}
            value={Number.isNaN(params.tasaMensual) ? "" : params.tasaMensual}
            onChange={(ev) => set({ tasaMensual: ev.target.valueAsNumber })}
            className="num"
          />
          {errores.tasa && <p className="text-xs text-destructive">{errores.tasa}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cuotas">{etiquetaPlazo}</Label>
          <Input
            id="cuotas"
            type="number"
            inputMode="decimal"
            min={1}
            max={120}
            step={1}
            value={Number.isNaN(params.cuotas) ? "" : params.cuotas}
            onChange={(ev) => set({ cuotas: ev.target.valueAsNumber })}
            className="num"
          />
          {errores.cuotas && <p className="text-xs text-destructive">{errores.cuotas}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fecha">Fecha de primer pago (opcional)</Label>
          <Input
            id="fecha"
            type="date"
            value={params.primerPago ?? ""}
            onChange={(ev) => set({ primerPago: ev.target.value })}
          />
        </div>
      </div>
    </Card>
  );
}
